import React, { useCallback, useMemo, useState } from 'react';
import moment from 'moment';
import { FormattedMessage, useIntl } from 'react-intl';
import withStyles from 'isomorphic-style-loader/withStyles';
import cx from 'classnames';

// Components
import CommonTable from '../../../CommonTable/CommonTable';
import CurrencyConverter from '../../../CurrencyConverter/CurrencyConverter';
import CustomPagination from '../../../CustomPagination/CustomPagination';
import Loader from '../../../Loader/Loader';
import CustomConfirmModal from '../../../Common/CustomConfirmModal/CustomConfirmModal';
import CancelServicePlanModalBody from '../CancelServicePlanModalBody';
import SearchFilterPlans from '../../SearchFilterPlans';
import NoDataSection from '../../NoDataSection';
import TableAction from '../../../CommonTable/TableAction';

// Helpers
import { photosShow } from '../../../../helpers/photosShow';
import { dateFormat, servicePlansUploadDir } from '../../../../config';
import messages from '../../../../locale/jsonMessages';
import { cancelPurchaseServicePlanAction } from '../../../../actions/ServicePlans/makeServicePlanPayment/makeServicePlanPayment';
import debounce from '../../../../helpers/debounce';
import { useDispatch } from 'react-redux';
import { getPurchaseServicePlanValue } from '../../../../helpers/GetDynamicItemName/dynamicHomeValue';

import { PlanHistoryArrow } from '../../../Common/Assets/SvgIconComponent/CommonSvgIcon';
import NoServicePlanDataImg from '/public/SiteIcons/noServicePlanHistory.svg';
import cs from '../../../../components/commonStyle.css';
import s from './PurchaseHistoryManagement.css';

const PurchaseHistoryManagement = (props) => {
    const { data: { getAllPurchaseServicePlans, loading, refetch }, } = props;

    const { formatMessage, locale } = useIntl();

    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState(null);
    const [load, setLoad] = useState(false);
    const [searchKey, setSearchKey] = useState("");
    const [purchaseUserType, setPurchaseUserType] = useState('renter')
    const [currentPage, setCurrentPage] = useState(1);
    const dispatch = useDispatch();


    const setStateVariable = (variables) => {
        setSearchKey(variables?.searchKey);
        setCurrentPage(variables?.currentPage)
        setPurchaseUserType(variables?.purchaseUserType)
    };

    const updateData = useCallback(
        (variables) => {
            setStateVariable(variables);
            refetch({
                userType: variables.purchaseUserType,
                searchKey: variables.searchKey,
                currentPage: variables.currentPage,
            });
        },
        [refetch, setStateVariable]
    );


    const handleSearchChange = useMemo(
        () =>
            debounce((value) => {
                updateData({ currentPage: 1, searchKey: value, purchaseUserType });
            }, 250),
        [purchaseUserType, updateData]
    );

    const tableFilter = [
        { value: 'renter', label: formatMessage(messages.guestPlansLabel) },
        { value: 'owner', label: formatMessage(messages.hostPlansLabel) },
    ];


    const handleCancel = async ({ id, currentPage }) => {
        try {
            setLoad(true);
            const pageResults = getAllPurchaseServicePlans?.results ?? [];
            const updatedPage =
                currentPage > 1 && pageResults.length === 1
                    ? currentPage - 1
                    : currentPage;
            await dispatch(cancelPurchaseServicePlanAction(id));
            setLoad(false);
            setShowModal(false);
            setModalData(null);
            updateData({ currentPage: updatedPage, searchKey, purchaseUserType });
        } catch (error) {
            setLoad(false);
        }
    };

    const handleDropDown = (e) => {
        const value = e.target.value;
        updateData({ purchaseUserType: value, searchKey, currentPage: 1 });
    };

    const paginationData = (newPage) => {
        updateData({ currentPage: newPage, searchKey, purchaseUserType });
    };

    let path = photosShow(servicePlansUploadDir);

    const openCancelModal = (value, currentPage) => {
        setModalData({ value, currentPage });
        setShowModal(true);
    };

    const thead = () => [
        { data: formatMessage(messages.planLabel) },
        { data: formatMessage(messages.priceLabel) },
        { data: formatMessage(messages.duration) },
        { data: formatMessage(messages.status) },
        { data: formatMessage(messages.StartDate) },
        { data: formatMessage(messages.EndDate) }
    ];

    const tbody = () =>
        getAllPurchaseServicePlans?.results?.map((value, key) => {
            const planDetails = JSON.parse(value?.dynamicServicePlanDetails || '[]');
            const matchPlan = getPurchaseServicePlanValue(planDetails, locale)
            const planName = matchPlan?.planName ? matchPlan?.planName : value?.servicePlanName;
            return {
                id: key,
                data: [
                    {
                        data: (
                            <div className={s.purchaseHistoryPlanInfo}>
                                <div className={s.purchaseHistoryPlanHead}>
                                    {value?.servicePlanData && (
                                        <div className={s.purchaseHistoryPlanImgBox}>
                                            <img src={path + value?.servicePlanData?.mediaImage} alt={value?.servicePlanName} className={s.purchaseHistoryPlanImg} />
                                        </div>
                                    )}
                                    <div className={s.purchaseHistoryPlanTitle}>{planName}</div>
                                </div>
                                <ul className={s.purchaseHistoryPlanFeatureList}>
                                    {value?.serviceFee >= 0 && (<li className={s.purchaseHistoryPlanListItem}>
                                        <PlanHistoryArrow className="purchaseHistoryPlanListItemSvgIcon" />
                                        <span>
                                            <span>{value?.serviceFee}%&nbsp;</span>
                                            <span>{formatMessage(messages.serviceFee)}</span>
                                        </span>

                                    </li>
                                    )}
                                    {value?.serviceAIFeature && (
                                        <li className={s.purchaseHistoryPlanListItem}>
                                            <PlanHistoryArrow className="purchaseHistoryPlanListItemSvgIcon" />
                                            <span><FormattedMessage {...messages.aiFeatureLabel} /></span>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        ),
                    },
                    {
                        data: (
                            <div className={cx(cs.displayFlex, cs.alignCenter, cs.spaceBetween)}>
                                <CurrencyConverter amount={value?.price} from={value?.currency} />
                                <TableAction
                                    showReceipt={true}
                                    receiptLink={`/users/service-receipt/${value?.id}`}
                                    showCancel={value?.servicePlanStatus == 'active' ? true : false}
                                    cancelAction={() => openCancelModal(value, currentPage)}
                                />
                            </div>
                        ),
                    },

                    {
                        data: (
                            <div>
                                {value?.duration}{' '}
                                {value?.duration == 1 ? formatMessage(messages.month) : formatMessage(messages.monthsLabel)}
                            </div>
                        ),
                    },
                    {
                        data:
                            value?.servicePlanStatus == 'active' ? (
                                <span className="verifiedText">
                                    {formatMessage(messages.activeLabel)}
                                </span>
                            ) : (value?.servicePlanStatus == 'cancelled' ? (
                                <span className="cancelStatusText">
                                    {formatMessage(messages.cancelledServicePlanLabel)}
                                </span>
                            ) : (
                                formatMessage(messages.expiredPlanLabel)
                            )
                            ),
                    },
                    { data: moment(parseInt(value?.startDate)).format(dateFormat?.shortDateFormat) },
                    { data: moment(parseInt(value?.endDate)).format(dateFormat?.shortDateFormat) }
                ],
            }
        });

    const dataResults = getAllPurchaseServicePlans?.results || [];
    const dataCount = getAllPurchaseServicePlans?.count || 0;

    const filteredResults = dataResults?.filter(item => {
        return item.servicePlanName.toLowerCase().includes(searchKey.toLowerCase());
    });

    return (
        <div>
            {loading && <Loader type="text" />}
            {showModal && modalData && (
                <CustomConfirmModal
                    showModel={showModal}
                    title={formatMessage(messages.cancelPlanLabel)}
                    closeModal={() => setShowModal(false)}
                    popupButtonLeftName={formatMessage(messages.cancel)}
                    popupButtonLeftFunction={() => setShowModal(false)}
                    loaderActionBtnText={formatMessage(messages.confirmLabel)}
                    load={load}
                    modalData={modalData}
                    loaderConfirmActionFunction={(value) =>
                        handleCancel({
                            id: value?.value?.id,
                            currentPage: currentPage,
                        })
                    }
                >
                    <CancelServicePlanModalBody modalData={modalData} />
                </CustomConfirmModal>
            )}
            {!loading && (
                <>
                    <SearchFilterPlans
                        handleSearchChange={handleSearchChange}
                        tableFilter={tableFilter}
                        placeholder={formatMessage(messages.searchPlansLabel)}
                        handleDropDown={handleDropDown}
                        selectedValue={purchaseUserType}
                    />
                    {dataCount === 0 && (
                        <NoDataSection
                            notFoundImg={NoServicePlanDataImg}
                            noDataTitle={formatMessage(messages.noServicePlanHistoryLabel)}
                            noDataDesc={formatMessage(messages.servicePlanHistoryContent)}
                            noDataNavBtnLink='/users/service-plans/renter'
                            navLinkBtnText={formatMessage(messages.servicePlanHistoryBtn)}
                            containerClass="servicePlanHistoryNoDataBox"
                        />
                    )}

                    {filteredResults?.length > 0 && (
                        <>
                            <CommonTable
                                thead={thead}
                                tbody={tbody}
                                className={'payoutListTable purchaseHistoryTable'}
                                isHeadingHide
                            />
                            <CustomPagination
                                total={getAllPurchaseServicePlans.count}
                                currentPage={currentPage}
                                defaultCurrent={1}
                                defaultPageSize={5}
                                change={paginationData}
                                paginationLabel={formatMessage(
                                    messages[
                                    getAllPurchaseServicePlans.count > 1
                                        ? 'servicePlans'
                                        : 'servicePlan'
                                    ]
                                )}
                                isScroll
                            />
                        </>
                    )}
                    {dataCount > 0 && filteredResults?.length === 0 && (
                        <NoDataSection
                            notFoundImg={NoServicePlanDataImg}
                            noDataTitle={formatMessage(messages.noServicePlanHistoryLabel)}
                            noDataDesc={formatMessage(messages.servicePlanHistoryContent)}
                            noDataNavBtnLink='/users/service-plans/renter'
                            navLinkBtnText={formatMessage(messages.servicePlanHistoryBtn)}
                            containerClass="servicePlanHistoryNoDataBox"
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default withStyles(cs, s)(PurchaseHistoryManagement);
